// AUTO-GENERATED from docs/TECHNICAL_PRD.md — CID Agent PWA API layer
// Module: My Listings (list, detail, create/edit, images, status/publish, visibility, share, convert)
// Every list (collection GET) takes params { page, limit, ...filters }.
import client from './client';

/**
 * GET /api/agent/listings [LIST · paginated]
 * My Listings list with status chips (DRAFT/ACTIVE/WITHDRAWN/EXPIRED) and INTERNAL/PUBLIC filter; each row carries cover_image_url.
 * @disposition ADAPT_SCOPE · maps to listing.service.listListings (routes GET /, controller.listListings)
 */
export const listListings = (params = {}, config = {}) => client.get(`/api/agent/listings`, { params, ...config });

/**
 * GET /api/agent/listings/search [autocomplete + picker feed]
 * Server-side search over transactable listings (ACTIVE / UNDER_OFFER, mine + other
 * agents') behind the "Link to a listing" pickers. Params: { q, limit, page,
 * listing_type, linkable }. Returns compact rows incl. cover_image_url, state,
 * property_type, owner details and the listing agent's name.
 *
 * `linkable=1` drops other agents' listings, leaving what the caller can actually
 * write against (their own + HQ stock). Every LINK picker passes it, because a sale
 * or booking form raised on a colleague's listing is refused server-side — co-broke
 * goes through a co-share request (api/coShare.js). Browsing leaves it off.
 *
 * `q` may be blank as long as a structured filter (e.g. listing_type) is supplied —
 * that's what lets ListingPickerSheet open on a browsable feed instead of an empty
 * box. Pages are `limit`-sized; a short page means the end of the feed.
 */
export const searchListings = (params = {}) => client.get(`/api/agent/listings/search`, { params });


/**
 * GET /api/agent/listings/:id
 * Listing detail: gallery, specs, owner info, linked ALS/ALR authorization form, and the agents array.
 * @disposition ADAPT_SCOPE · maps to listing.service.getListingById (routes GET /:id, controller.getListingById)
 */
export const getListingsById = (id, params = {}) => client.get(`/api/agent/listings/${id}`, { params });

/**
 * POST /api/agent/listings
 * Create a SUBSALE/RENT listing that starts DRAFT + INTERNAL and is owned by the caller.
 * @disposition BUILD_NEW · maps to listing.service.createListing (routes POST /, controller.createListing)
 */
export const createListings = (data = {}) => client.post(`/api/agent/listings`, data);

/**
 * PUT /api/agent/listings/:id
 * Edit own listing's specs/owner info/authorization link.
 * @disposition ADAPT_SCOPE · maps to listing.service.updateListing (routes PUT /:id, controller.updateListing)
 */
export const updateListingsById = (id, data = {}) => client.put(`/api/agent/listings/${id}`, data);

/**
 * PUT /api/agent/listings/:id/status
 * Agent-driven lifecycle: publish ({ status: 'ACTIVE' } from DRAFT) / withdraw /
 * expire / reactivate own listing. Publishing is this call — there is no HQ
 * approval step and so no request-approval or recall endpoint.
 * @disposition ADAPT_SCOPE · maps to listing.service.updateListingStatus (routes PUT /:id/status, controller.updateStatus)
 */
export const updateListingsStatusById = (id, data = {}) => client.put(`/api/agent/listings/${id}/status`, data);

/**
 * PUT /api/agent/listings/:id/visibility
 * Agent lists / unlists their own property: { visibility: 'INTERNAL' | 'PUBLIC' }.
 * Rejected while the listing is still a DRAFT — a draft is private and has no
 * visibility to set until it is published.
 * @disposition BUILD_NEW · maps to listing.service.updateVisibility (routes PUT /:id/visibility, controller.updateVisibility)
 */
export const updateListingsVisibilityById = (id, data = {}) => client.put(`/api/agent/listings/${id}/visibility`, data);

/**
 * POST /api/agent/listings/:id/images
 * Upload gallery images (multipart, up to 10) for own listing.
 * @disposition ADAPT_SCOPE · maps to listing.service.uploadImages (routes POST /:id/images, controller.uploadImages)
 */
export const createListingsImagesById = (id, data = {}) => client.post(`/api/agent/listings/${id}/images`, data);

// Signed-URL photo upload (public 'listing-images' bucket): upload-url → PUT to storage → confirm.
export const createListingsImagesUploadUrlById = (id, data = {}) => client.post(`/api/agent/listings/${id}/images/upload-url`, data);
export const createListingsImagesConfirmUploadById = (id, data = {}) => client.post(`/api/agent/listings/${id}/images/confirm-upload`, data);
export const listListingsImages = (id, params = {}) => client.get(`/api/agent/listings/${id}/images`, { params });

/**
 * DELETE /api/agent/listings/:id/images/:imageId
 * Remove an image from own listing.
 * @disposition ADAPT_SCOPE · maps to listing.service.deleteImage (routes DELETE /:id/images/:imageId, controller.deleteImage)
 */
export const deleteListingsImagesByIdImageId = (id, imageId, config = {}) => client.delete(`/api/agent/listings/${id}/images/${imageId}`, config);

/**
 * PUT /api/agent/listings/:id/images/:imageId/cover
 * Set the cover image for own listing.
 * @disposition ADAPT_SCOPE · maps to listing.service.setCoverImage (routes PUT /:id/images/:imageId/cover, controller.setCoverImage)
 */
export const updateListingsImagesCoverByIdImageId = (id, imageId, data = {}) => client.put(`/api/agent/listings/${id}/images/${imageId}/cover`, data);

/**
 * PUT /api/agent/listings/:id/images/reorder
 * Persist drag-reorder of the gallery.
 * @disposition BUILD_NEW · maps to (none — display_order exists but no reorder endpoint)
 */
export const updateListingsImagesReorderById = (id, data = {}) => client.put(`/api/agent/listings/${id}/images/reorder`, data);

/**
 * POST /api/agent/listings/:id/share
 * Produce a shareable public link for a PUBLIC listing (share button).
 * @disposition BUILD_NEW · maps to (none — no public-share action today)
 */
export const createListingsShareById = (id, data = {}) => client.post(`/api/agent/listings/${id}/share`, data);

/**
 * POST /api/agent/listings/:id/convert
 * Convert a listing into a new booking form or a draft sale, prefilled from listing (owner, authorization_form_id, specs).
 * @disposition BUILD_NEW · maps to (none — no listing→booking/sale bridge in listing.service)
 */
export const createListingsConvertById = (id, data = {}) => client.post(`/api/agent/listings/${id}/convert`, data);

/**
 * GET /api/agent/listings/postcode-lookup/:postcode
 * Postcode → state/city/area helper for the create/edit form.
 * @disposition REUSE_AS_IS · maps to inline route handler using findLocationByPostcode (routes GET /postcode-lookup/:postcode)
 */
export const getListingsPostcodeLookupByPostcode = (postcode, params = {}) => client.get(`/api/agent/listings/postcode-lookup/${postcode}`, { params });

/**
 * GET /api/agent/listings/authorization-forms?listing_type=SUBSALE|RENT&listing_id=
 * The agent's own APPROVED ALS/ALR letters that are free to link to a listing —
 * one already attached to a different listing is filtered out. Each row carries the
 * values the listing form autofills (address, owner, price, furnish).
 */
export const listListingAuthorizationForms = (params = {}) => client.get(`/api/agent/listings/authorization-forms`, { params });

/**
 * DELETE /api/agent/listings/:id
 * Discard an own DRAFT listing.
 * @disposition ADAPT_SCOPE · maps to listing.service.deleteListing (routes DELETE /:id, controller.deleteListing)
 */
export const deleteListingsById = (id, config = {}) => client.delete(`/api/agent/listings/${id}`, config);
